/**
 * SDataTable — canonical data-table primitive for the Segmently UI.
 *
 * Thin wrapper over Material React Table (MRT, v3) that encodes the project's
 * UI theme policy as defaults. Consumers pass columns + data + behaviour
 * flags (enableRowSelection, enableRowActions, etc.) and get zero-config
 * visual parity with the Audiences list and Tasks tab.
 *
 * Locked defaults (all overridable via passthrough props):
 * - No MRT chrome: no column-action icons, density toggle, fullscreen,
 *   hiding menu, global filter, column filters, bottom toolbar.
 * - `enablePagination: false` — pagination is opt-in. MRT's upstream default
 *   is ON with pageSize 10, which combined with our hidden bottom toolbar
 *   silently truncated >10-row tables to their first page. Consumers that
 *   pass `enablePagination` get the themed bottom toolbar auto-unlocked.
 * - `selectAllMode: 'all'` — the header checkbox selects the WHOLE dataset,
 *   not just the visible page (MRT's default is page-scoped).
 * - Outer Paper: variant="outlined" + elevation={1}  (Audiences style).
 * - Compact table size; 2px head divider with `action.hover` background;
 *   1px row divider; theme-driven hover.
 * - Row actions column positioned on the right; empty label.
 * - Select column (any row-selection table): a fixed 24px gutter — 8px
 *   padding + a 16px compact checkbox — that never grows with the window;
 *   the first data cell starts 8px after the checkbox, so the gap after the
 *   checkbox equals the gap before it. The checkbox recedes to
 *   `text.disabled` until it is checked, and its CLICK area stays 28×36px
 *   regardless of the box size. Caller checkbox props merge on top.
 *   Keeping that gutter fixed in semantic layout needs one `width: auto`
 *   column to absorb the leftover width: `grow: true` names it, and a
 *   selectable table that names none gets its first column by default.
 * - No MRT alert banner — selection counters live in caller-supplied
 *   top-toolbar content via `renderTopToolbarCustomActions`.
 *
 * Inline-header search (Phase 2): opt-in via `enableSearch` + `searchInColumn`.
 * SDataTable wraps the chosen column's `Header` with `ExpandingHeaderSearch`
 * (icon-only-expanding TextField) and wires the query to MRT's filter engine.
 * `searchMode: 'column'` (default) uses `column.setFilterValue`; `'global'`
 * uses `table.setGlobalFilter`. MRT's default filter/search chrome is
 * suppressed via `columnFilterDisplayMode: 'custom'` / `positionGlobalFilter:
 * 'none'` so we render the only visible input.
 *
 * Header-checkbox selection reveal (Phase 4): opt-in via `checkboxReveal` —
 * the "Gmail pattern" selection entry. The header select-all checkbox is
 * always visible in the leading column; body checkboxes reveal on row hover
 * / row selected / any selection present; Escape clears the selection; row
 * click keeps navigating (select-cell clicks are swallowed). Requires
 * controlled selection state (`state.rowSelection` + `onRowSelectionChange`,
 * canonically `useSelectionMode({ defaultMode: true })`). Mutually exclusive
 * with the `selectionMode` transformable-button entry.
 *
 * All sx values reference theme tokens (`divider`, `action.hover`,
 * `fontWeightMedium`). Zero hardcoded colours, zero px-size fonts.
 *
 * @example
 * ```tsx
 * import { SDataTable } from '@modules/ui-components';
 * import type { SDataTableColumnDef } from '@modules/ui-components';
 *
 * const columns: SDataTableColumnDef<Task>[] = [
 *   { accessorKey: 'name', header: 'Name' },
 *   { accessorKey: 'description', header: 'Description' },
 * ];
 *
 * <SDataTable
 *   columns={columns}
 *   data={tasks}
 *   enableRowSelection
 *   enableRowActions
 *   getRowId={(row) => row.id}
 *   enableSearch
 *   searchInColumn="name"
 *   searchMode="global"
 *   renderRowActionMenuItems={({ row, closeMenu }) => [...]}
 * />
 * ```
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { alpha } from '@mui/material/styles';
import { MaterialReactTable } from 'material-react-table';
import type { MRT_ColumnDef } from 'material-react-table';
import type { SDataTableProps } from './SDataTable.types';
import { ExpandingHeaderSearch } from './ExpandingHeaderSearch';

// ----------------------------------------------------------------------------
// Row-selection column geometry — the default for EVERY selectable table
// (sidebar-subscription plan C-15 / D-14 / D-22).
// ----------------------------------------------------------------------------
// The select column is a fixed gutter: 8px cell padding + a 16px compact
// checkbox + 0 right padding = 24px, at any window width. The first data cell
// starts 8px after the checkbox, so gap(checkbox → first content) equals
// gap(cell edge → checkbox). Everything is keyed off a class on the select
// cells (not a column index), so it holds whatever display columns lead.

/** Left gutter of the select cell AND left padding of the first data cell. */
export const SDATATABLE_SELECT_GUTTER_PX = 8;
/** Rendered size of the compact select checkbox. */
export const SDATATABLE_SELECT_CHECKBOX_PX = 16;
/** Total width of the select column (gutter + checkbox, no right padding). */
export const SDATATABLE_SELECT_COLUMN_PX =
  SDATATABLE_SELECT_GUTTER_PX + SDATATABLE_SELECT_CHECKBOX_PX;
/**
 * Clickable size of the checkbox. Deliberately DECOUPLED from the visual box:
 * shrinking the box must not shrink the target, so these are stated, not
 * derived. 28×36 is the size the control shipped with when the box was 20px.
 */
export const SDATATABLE_SELECT_HIT_WIDTH_PX = 28;
export const SDATATABLE_SELECT_HIT_HEIGHT_PX = 36;
/**
 * Symmetric vertical padding of a head cell. MRT's own is asymmetric and
 * differs per cell, which knocks the select checkbox off the labels' midline.
 */
export const SDATATABLE_HEAD_CELL_PAD_Y_PX = 12;
/** Class MRT puts on every select-column cell (head + body). */
export const SDATATABLE_SELECT_CELL_CLASS = 'SDataTable-selectCell';
/**
 * Class on the cells of a data column declared `grow: true`. In semantic
 * (table) layout that column takes the spare width (`width: auto`) and the
 * other columns keep their `size` — the MRT `grow` contract grid layouts
 * already honour. Opt-in per column (e.g. a list's Name column); a selectable
 * table that opts no column in gets its first column tagged by default, so the
 * select gutter always has somewhere else to send the leftover width.
 */
export const SDATATABLE_GROW_CELL_CLASS = 'SDataTable-growCell';

type SxObject = Record<string, unknown>;
type SxInput = SxObject | ((theme: unknown) => SxObject) | SxInput[] | undefined | null | false;

/**
 * Resolve an sx value (object / theme callback / array) into ONE object.
 * MRT spreads caller `sx` into its own object (`{ ...mrtSx, ...callerSx }`),
 * so an sx ARRAY passed to MRT would be spread as keys "0", "1" — always hand
 * MRT a single object or a callback returning one.
 */
function resolveSx(sx: SxInput, theme: unknown): SxObject {
  if (!sx) return {};
  if (Array.isArray(sx)) {
    return sx.reduce<SxObject>((acc, part) => ({ ...acc, ...resolveSx(part, theme) }), {});
  }
  if (typeof sx === 'function') return sx(theme) ?? {};
  return sx;
}

/** Later entries win (shallow per key), returned as an MRT-safe theme callback. */
function mergeSx(...parts: SxInput[]): (theme: unknown) => SxObject {
  return (theme) => resolveSx(parts, theme);
}

/**
 * Table-level CSS for the select column. Lives on the TABLE (descendant
 * selectors outrank the cells' own single-class styles), so the caller's
 * table-level head/body cell props keep styling the select cells too.
 */
function buildSelectColumnTableSx(layoutMode: unknown): SxObject {
  const isGridLayout = typeof layoutMode === 'string' && layoutMode.startsWith('grid');
  const width = isGridLayout
    ? {
        // Grid layouts: MRT cells are flex items — pin the basis, never grow.
        width: SDATATABLE_SELECT_COLUMN_PX,
        minWidth: SDATATABLE_SELECT_COLUMN_PX,
        maxWidth: SDATATABLE_SELECT_COLUMN_PX,
        flex: `0 0 ${SDATATABLE_SELECT_COLUMN_PX}px`,
      }
    : {
        // Semantic (table) layout: state the width as a LENGTH.
        //
        // `width: 0%` pinned the column in Blink, but the auto table algorithm
        // treats percentage columns differently across engines: WebKit hands a
        // percentage column the table's ENTIRE leftover width. Measured in
        // Safari on a 1 000px table (2026-10-08): the 28px select gutter
        // rendered 540px wide and shoved the first data column to the middle of
        // the table. A length pins the column in both engines — provided some
        // other column is `width: auto` to absorb the slack, which is what
        // `grow: true` declares and what `markFirstLeafColumn` guarantees for
        // selectable tables that declare none.
        width: SDATATABLE_SELECT_COLUMN_PX,
        minWidth: SDATATABLE_SELECT_COLUMN_PX,
        maxWidth: SDATATABLE_SELECT_COLUMN_PX,
      };
  const selectCell = `& .${SDATATABLE_SELECT_CELL_CLASS}`;
  return {
    [selectCell]: {
      ...width,
      boxSizing: 'border-box',
      pl: `${SDATATABLE_SELECT_GUTTER_PX}px`,
      pr: 0,
    },
    // MRT gives every header label a `min(header.length, 4)ch` min-width
    // (≈32px for "select"), which would widen the gutter.
    [`${selectCell} .Mui-TableHeadCell-Content-Wrapper`]: { minWidth: 0 },
    // First data cell: content starts one gutter after the checkbox.
    [`${selectCell} + th, ${selectCell} + td`]: {
      pl: `${SDATATABLE_SELECT_GUTTER_PX}px`,
    },
    // Semantic layout: a `grow: true` column is the auto column that absorbs
    // the spare width; fixed-width columns keep their size.
    ...(isGridLayout
      ? {}
      : { [`& .${SDATATABLE_GROW_CELL_CLASS}`]: { width: 'auto' } }),
  };
}

type CellPropsInput =
  | Record<string, unknown>
  | ((args: unknown) => Record<string, unknown> | undefined)
  | undefined;

/** Add a class to a column's head/body cell props (object or callback). */
function withCellClass(callerProps: CellPropsInput, className: string) {
  const merge = (base: Record<string, unknown> | undefined) => ({
    ...base,
    className: [base?.className, className].filter(Boolean).join(' '),
  });
  if (typeof callerProps === 'function') {
    return (args: unknown) => merge(callerProps(args));
  }
  return merge(callerProps);
}

/** Add the spare-width class to one leaf column definition. */
function withGrowCellClass<T>(def: Record<string, unknown>): T {
  return {
    ...def,
    muiTableHeadCellProps: withCellClass(
      def.muiTableHeadCellProps as CellPropsInput,
      SDATATABLE_GROW_CELL_CLASS,
    ),
    muiTableBodyCellProps: withCellClass(
      def.muiTableBodyCellProps as CellPropsInput,
      SDATATABLE_GROW_CELL_CLASS,
    ),
  } as T;
}

/**
 * Tag the cells of `grow: true` leaf columns (see SDATATABLE_GROW_CELL_CLASS).
 * Returns the same array when no column opts in (zero-cost path).
 */
function markGrowColumns<T>(columns: T[] | undefined): T[] | undefined {
  if (!columns) return columns;
  let changed = false;
  const next = columns.map((col) => {
    const def = col as Record<string, unknown>;
    if (Array.isArray(def.columns)) {
      const children = markGrowColumns(def.columns as unknown[]);
      if (children === def.columns) return col;
      changed = true;
      return { ...def, columns: children } as T;
    }
    if (def.grow !== true) return col;
    changed = true;
    return withGrowCellClass<T>(def);
  });
  return changed ? next : columns;
}

/** Does any leaf column opt into `grow: true`? */
function hasGrowColumn(columns: unknown[] | undefined): boolean {
  if (!columns) return false;
  return columns.some((col) => {
    const def = col as Record<string, unknown>;
    if (Array.isArray(def.columns)) return hasGrowColumn(def.columns as unknown[]);
    return def.grow === true;
  });
}

/**
 * Tag the FIRST leaf column as the spare-width absorber.
 *
 * A selectable semantic table needs ONE `width: auto` column, or the auto table
 * algorithm spreads the leftover width over every column — the select gutter
 * included — and the fixed 28px gutter stops being fixed. Callers say which
 * column that is with `grow: true`; this is the default for the ones that do
 * not, and the first column (the name/title) is where list tables want their
 * slack anyway.
 */
function markFirstLeafColumn<T>(columns: T[] | undefined): T[] | undefined {
  if (!columns || columns.length === 0) return columns;
  let done = false;
  const walk = (cols: unknown[]): unknown[] =>
    cols.map((col) => {
      if (done) return col;
      const def = col as Record<string, unknown>;
      if (Array.isArray(def.columns)) {
        const children = walk(def.columns as unknown[]);
        return children === def.columns ? col : { ...def, columns: children };
      }
      done = true;
      return withGrowCellClass(def);
    });
  const next = walk(columns) as T[];
  return done ? next : columns;
}

/**
 * Compact select checkbox: MRT hard-codes 2.5rem (40px) + a -0.4rem margin
 * unless density is 'compact' — lock it to the 16px visual box so the gutter
 * stays 24px. Without the ripple there is no default focus styling, so the
 * keyboard focus ring is drawn inside the box (the cell clips overflow).
 */
/**
 * How far the resting (unchecked) checkbox is stepped back from the theme's
 * own `text.secondary`. Derived from the token rather than hardcoded, so the
 * step is the same in both modes: it lands at 4.7:1 on the dark surface and
 * 3.6:1 on the light one, both clear of the 3:1 floor WCAG 1.4.11 sets for a
 * control. (A flat `text.disabled` would have been 4.7:1 dark but **2.7:1**
 * light — below the floor — and is the tone a non-selectable row already uses.)
 */
const SDATATABLE_SELECT_RESTING_ALPHA = 0.75;

const COMPACT_SELECT_CHECKBOX_SX = (theme: unknown): SxObject => ({
  p: 0,
  m: 0,
  width: SDATATABLE_SELECT_CHECKBOX_PX,
  minWidth: SDATATABLE_SELECT_CHECKBOX_PX,
  maxWidth: SDATATABLE_SELECT_CHECKBOX_PX,
  height: SDATATABLE_SELECT_CHECKBOX_PX,
  minHeight: SDATATABLE_SELECT_CHECKBOX_PX,
  maxHeight: SDATATABLE_SELECT_CHECKBOX_PX,
  flex: `0 0 ${SDATATABLE_SELECT_CHECKBOX_PX}px`,
  overflow: 'visible',
  // Hit target: the invisible input reaches into the left gutter and above/below the box, so the
  // clickable area is 28×36px (≥ 24×24, WCAG 2.5.8) while the visual box stays 16px.
  //
  // The size is stated as a LENGTH, never `auto` stretched between offsets. A checkbox `<input>`
  // is a REPLACED element, and CSS 2.1 §10.3.8 / §10.6.5 say an absolutely positioned replaced
  // box with `width`/`height: auto` takes its INTRINSIC size and drops the over-constrained
  // `right`/`bottom`. Blink stretches form controls anyway; WebKit follows the spec, so the
  // earlier `auto` + four offsets collapsed the input to the native ~12×12 box parked 8px up and
  // left of the visual checkbox. In Safari the click then landed on the SVG icon instead of the
  // input and row selection did nothing at all (2026-10-08).
  '& .PrivateSwitchBase-input': {
    // Reaches back over the left gutter and is centred vertically on the box.
    top: -(SDATATABLE_SELECT_HIT_HEIGHT_PX - SDATATABLE_SELECT_CHECKBOX_PX) / 2,
    left: -SDATATABLE_SELECT_GUTTER_PX,
    right: 'auto',
    bottom: 'auto',
    width: SDATATABLE_SELECT_HIT_WIDTH_PX,
    height: SDATATABLE_SELECT_HIT_HEIGHT_PX,
  },
  '& .MuiTouchRipple-root': {
    inset: 0,
    width: SDATATABLE_SELECT_CHECKBOX_PX,
    height: SDATATABLE_SELECT_CHECKBOX_PX,
  },
  '& .MuiSvgIcon-root': { display: 'block', fontSize: SDATATABLE_SELECT_CHECKBOX_PX },
  '&.Mui-focusVisible': {
    outline: '2px solid',
    outlineColor: 'primary.main',
    outlineOffset: '-2px',
  },
  // Resting tone — head and body alike, so the always-visible header select-all
  // of a `checkboxReveal` table does not read louder than the rows under it.
  // Checked and indeterminate keep the theme's primary and a non-selectable row
  // keeps `action.disabled`, so all three states stay clearly apart.
  '&:not(.Mui-checked):not(.MuiCheckbox-indeterminate):not(.Mui-disabled)': {
    color: alpha(
      (theme as { palette: { text: { secondary: string } } }).palette.text.secondary,
      SDATATABLE_SELECT_RESTING_ALPHA,
    ),
  },
});

type CheckboxPropsInput =
  | SxObject
  | ((args: unknown) => SxObject | undefined)
  | undefined;

/**
 * Layer the compact checkbox defaults UNDER the caller's checkbox props
 * (object or per-row callback): caller keys win, caller `sx` merges on top.
 */
function withCompactCheckbox(callerProps: CheckboxPropsInput) {
  return (args: unknown) => {
    const base =
      (typeof callerProps === 'function' ? callerProps(args) : callerProps) ?? {};
    return {
      size: 'small' as const,
      disableRipple: true,
      ...base,
      sx: mergeSx(COMPACT_SELECT_CHECKBOX_SX, (base as { sx?: SxInput }).sx),
    };
  };
}

/**
 * Locked MRT defaults — constitute our UI theme policy for tables.
 *
 * Spread FIRST in the JSX below so consumer props can override when an edge
 * case warrants it (explicit intent required; we do not silently fall back).
 */
const SDATATABLE_DEFAULTS = {
  // --- Suppress MRT chrome not present in our current UX ---
  enableColumnActions: false,
  enableColumnFilters: false,
  enableDensityToggle: false,
  enableFullScreenToggle: false,
  enableHiding: false,
  enableGlobalFilter: false,
  enableTopToolbar: true,
  enableBottomToolbar: false,
  // MRT defaults pagination ON (pageSize 10); with the bottom toolbar hidden
  // above, tables with >10 rows rendered only the first page with no page
  // controls at all. Pagination is therefore opt-in here.
  enablePagination: false,

  // --- Selection: header checkbox covers the whole dataset, not the page ---
  selectAllMode: 'all' as const,

  // --- Row actions + toolbar positioning ---
  positionActionsColumn: 'last' as const,
  positionToolbarAlertBanner: 'none' as const,

  // --- Outer Paper: Audiences-style outlined border.
  // Note: MUI's outlined variant deliberately sets elevation=0 and ignores
  // the `elevation` prop. We intentionally omit `elevation` here to avoid a
  // React dev-console warning on every mount. The visual result matches the
  // Audiences list (AudienceManagementSection.jsx:693) which, despite its
  // `elevation={1}` prop, also renders without a shadow for the same reason.
  muiTablePaperProps: {
    variant: 'outlined' as const,
    // MRT's internal default is elevation=2; MUI emits a dev warning when
    // `variant="outlined"` is combined with any non-zero elevation ("has no
    // effect"). We lock elevation=0 explicitly to silence the warning while
    // keeping the Audiences-style outlined border.
    elevation: 0 as const,
  },

  // --- Table density ---
  muiTableProps: {
    size: 'small' as const,
  },

  // --- Head cell: Tasks-style 2px divider + hover-bg ---
  muiTableHeadCellProps: {
    sx: {
      bgcolor: 'action.hover',
      borderBottom: '2px solid',
      borderColor: 'divider',
      fontWeight: 'fontWeightMedium',
      // Centre head-cell content vertically, as body cells already do.
      //
      // MRT ships `vertical-align: top` AND an asymmetric vertical padding
      // (12px top, 0–9.6px bottom, different per cell), so the select checkbox
      // and the labels beside it share a midline only when their content boxes
      // happen to be the same height. The moment one header hosts something
      // taller — a search field, a two-line label — they drift apart and read
      // as bottom-aligned. Centring alone is not enough: `vertical-align`
      // centres inside the CONTENT box, so the uneven padding would still
      // offset the checkbox. Both have to be stated.
      verticalAlign: 'middle',
      pt: `${SDATATABLE_HEAD_CELL_PAD_Y_PX}px`,
      pb: `${SDATATABLE_HEAD_CELL_PAD_Y_PX}px`,
    },
  },

  // --- Body cell: 1px divider separator ---
  muiTableBodyCellProps: {
    sx: {
      borderBottom: '1px solid',
      borderColor: 'divider',
    },
  },

  // --- Body row: theme-driven hover ---
  muiTableBodyRowProps: {
    hover: true,
  },

  // --- Checkboxes: compact 20px box in a fixed 28px select column. Applied
  // in the component (merged UNDER caller props), see `withCompactCheckbox`
  // and `buildSelectColumnTableSx`.

  // --- Actions column: labeled "Actions" header.
  // Consumers can override to empty (or localized text) via
  // `localization={{ actions: '' }}` on the SDataTable instance.
  localization: {
    actions: 'Actions',
  },
};

/**
 * SDataTable component.
 *
 * Destructures the custom search props out of `props` (they are not MRT
 * options and must not be forwarded). Everything else passes through to
 * `MaterialReactTable` with our locked defaults applied first.
 */
function SDataTableInner<TData extends Record<string, any>>(
  props: SDataTableProps<TData>,
) {
  const {
    // Phase 2 — search
    enableSearch = false,
    searchInColumn,
    searchMode = 'column',
    searchPlaceholder = 'Search...',
    // Phase 3 — selection mode
    selectionMode: controlledSelectionMode,
    defaultSelectionMode = false,
    // Phase 4 — Gmail-style header-checkbox selection entry
    checkboxReveal = false,
    // Column / caller row props are consumed here to layer selectionMode on top.
    columns: originalColumns,
    muiTableBodyRowProps: callerRowProps,
    muiTableProps: callerTableProps,
    // Select-column props are merged with the fixed-gutter defaults below.
    muiSelectCheckboxProps: callerSelectCheckboxProps,
    muiSelectAllCheckboxProps: callerSelectAllCheckboxProps,
    displayColumnDefOptions: callerDisplayColumnDefOptions,
    ...mrtProps
  } = props;

  // Uncontrolled fallback — only used when caller doesn't pass `selectionMode`.
  // Canonical adoption wires `useSelectionMode` in the parent, in which case
  // `controlledSelectionMode` is always defined and this internal state is
  // effectively dormant (React preserves it across renders but we never read
  // it when controlled).
  const [internalSelectionMode] = useState(defaultSelectionMode);
  const effectiveSelectionMode =
    controlledSelectionMode ?? internalSelectionMode;

  // Dev-only guard: `enableSearch` without `searchInColumn` is meaningless.
  // Degrade to no-op (do not crash) and warn once per mount path.
  if (
    enableSearch &&
    !searchInColumn &&
    process.env.NODE_ENV !== 'production'
  ) {
    // eslint-disable-next-line no-console
    console.warn(
      '[SDataTable] `enableSearch` is true but `searchInColumn` is missing — the search input will not render. Pass `searchInColumn` (accessorKey of the column whose header hosts the input).',
    );
  }

  // Transform columns only when search is actually armed. This keeps the
  // zero-cost path for all existing callers (they pay no extra useMemo work
  // because the identity of `originalColumns` is returned directly).
  const transformedColumns = useMemo(() => {
    if (!enableSearch || !searchInColumn || !originalColumns) {
      return originalColumns;
    }

    return originalColumns.map((col) => {
      const accessorKey = (col as { accessorKey?: string }).accessorKey;
      if (accessorKey !== searchInColumn) return col;

      // Preserve the original label — we render it as the collapsed-state
      // text next to the search icon. If `header` is a function (custom
      // render), we fall back to the column id; custom Header renders are an
      // advanced use that doesn't mix with our inline-search pattern today.
      const label: React.ReactNode =
        typeof col.header === 'function'
          ? (col as { id?: string }).id ?? accessorKey
          : (col.header as React.ReactNode);

      return {
        ...col,
        Header: ({
          column,
          table,
        }: {
          column: {
            getFilterValue: () => unknown;
            setFilterValue: (value: unknown) => void;
          };
          table: {
            getState: () => { globalFilter: unknown };
            setGlobalFilter: (value: unknown) => void;
          };
        }) => {
          const filterValue =
            searchMode === 'column'
              ? ((column.getFilterValue() as string) ?? '')
              : ((table.getState().globalFilter as string) ?? '');
          const setFilter =
            searchMode === 'column'
              ? (next: string) =>
                  column.setFilterValue(next === '' ? undefined : next)
              : (next: string) =>
                  table.setGlobalFilter(next === '' ? undefined : next);

          return (
            <ExpandingHeaderSearch
              label={label}
              placeholder={searchPlaceholder}
              value={filterValue}
              onChange={setFilter}
            />
          );
        },
      };
    });
  }, [
    originalColumns,
    enableSearch,
    searchInColumn,
    searchMode,
    searchPlaceholder,
  ]);

  // --------------------------------------------------------------------
  // Selection-mode plumbing
  // --------------------------------------------------------------------
  // When `effectiveSelectionMode` is true:
  //   - Force `enableRowSelection: true` so MRT renders checkboxes (a caller
  //     passing its own `enableRowSelection` — e.g. a per-row predicate that
  //     excludes folder rows — wins, since consumer props spread last).
  //   - Override `muiTableBodyRowProps.onClick` so a row click toggles that
  //     row's selection (mirrors Gmail / Drive "select mode"). Caller's own
  //     onClick is dropped while in mode for SELECTABLE rows only; rows the
  //     per-row predicate excludes (`row.getCanSelect() === false`) keep the
  //     caller's onClick, so e.g. folder rows stay navigable in mode.
  // When false: `callerRowProps` is forwarded untouched — full backward
  // compatibility with tables that don't use the selection-mode pattern
  // (including the current TasksTable pre-migration).
  const selectionModeFlags: Record<string, unknown> = effectiveSelectionMode
    ? { enableRowSelection: true }
    : {};

  // --------------------------------------------------------------------
  // checkboxReveal plumbing — the "Gmail pattern" selection entry
  // --------------------------------------------------------------------
  // The header select-all checkbox is the ONLY always-visible selection
  // control; body checkboxes reveal on row hover / row selected / any
  // selection present. Row click keeps the caller's behaviour (navigation)
  // at all times — the select CELL swallows its clicks instead.
  if (
    checkboxReveal &&
    effectiveSelectionMode &&
    process.env.NODE_ENV !== 'production'
  ) {
    // eslint-disable-next-line no-console
    console.warn(
      '[SDataTable] `checkboxReveal` and `selectionMode` are mutually exclusive selection entries — `checkboxReveal` wins; drop the `selectionMode` prop.',
    );
  }

  // Controlled selection state is required for the reveal-all-when-selected
  // behaviour (and is the canonical adoption anyway, via `useSelectionMode`).
  const rowSelectionState = (
    mrtProps as { state?: { rowSelection?: Record<string, boolean> } }
  ).state?.rowSelection;
  const revealHasSelection = useMemo(
    () =>
      checkboxReveal &&
      Object.values(rowSelectionState ?? {}).some(Boolean),
    [checkboxReveal, rowSelectionState],
  );

  // A caller-passed per-row predicate spreads after this and wins. Rows the
  // predicate excludes keep their DISABLED checkbox VISIBLE — an absent
  // control reads as a rendering bug on folder-heavy pages; a disabled one
  // reads as "not selectable".
  const checkboxRevealFlags: Record<string, unknown> = checkboxReveal
    ? { enableRowSelection: true }
    : {};

  // --------------------------------------------------------------------
  // Select column — fixed 28px gutter for EVERY selectable table (C-15)
  // --------------------------------------------------------------------
  // Harmless when the table has no select column (MRT ignores the options;
  // the table CSS matches nothing). Caller options for 'mrt-row-select' win.
  const selectColumnFlags = useMemo<Record<string, unknown>>(() => {
    const callerSelectColumnOptions = (
      callerDisplayColumnDefOptions as Record<string, Record<string, unknown>> | undefined
    )?.['mrt-row-select'];
    return {
      displayColumnDefOptions: {
        ...(callerDisplayColumnDefOptions as Record<string, unknown> | undefined),
        'mrt-row-select': {
          size: SDATATABLE_SELECT_COLUMN_PX,
          minSize: SDATATABLE_SELECT_COLUMN_PX,
          maxSize: SDATATABLE_SELECT_COLUMN_PX,
          grow: false,
          // className only — no sx here: a column-level sx would REPLACE the
          // table-level head/body cell sx (MRT merges props shallowly).
          muiTableHeadCellProps: { className: SDATATABLE_SELECT_CELL_CLASS },
          muiTableBodyCellProps: checkboxReveal
            ? {
                className: SDATATABLE_SELECT_CELL_CLASS,
                // MRT's checkbox stopPropagations its own clicks — swallow
                // the remaining cell-padding clicks so they never bubble
                // into the row onClick (navigation).
                onClick: (event: React.MouseEvent) => event.stopPropagation(),
              }
            : { className: SDATATABLE_SELECT_CELL_CLASS },
          ...callerSelectColumnOptions,
        },
      },
      muiSelectCheckboxProps: withCompactCheckbox(
        callerSelectCheckboxProps as CheckboxPropsInput,
      ),
      muiSelectAllCheckboxProps: withCompactCheckbox(
        callerSelectAllCheckboxProps as CheckboxPropsInput,
      ),
    };
  }, [
    callerDisplayColumnDefOptions,
    callerSelectCheckboxProps,
    callerSelectAllCheckboxProps,
    checkboxReveal,
  ]);

  // Escape with a selection → clear (Gmail exit). Inputs, dialogs and menus
  // own their Escape (search fields clear themselves, MUI modals close), so
  // only a "bare" Escape reaches this handler. Clearing goes through the
  // caller's `onRowSelectionChange` — the wrapper never owns selection state.
  const onRowSelectionChangeRef = useRef(
    (mrtProps as { onRowSelectionChange?: (next: Record<string, boolean>) => void })
      .onRowSelectionChange,
  );
  onRowSelectionChangeRef.current = (
    mrtProps as { onRowSelectionChange?: (next: Record<string, boolean>) => void }
  ).onRowSelectionChange;

  useEffect(() => {
    if (!checkboxReveal || !revealHasSelection) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      const target = event.target as HTMLElement | null;
      if (target) {
        if (
          target.closest?.(
            'textarea, [contenteditable="true"], [role="dialog"], [role="menu"]',
          )
        ) {
          return;
        }
        // Text-entry inputs own their Escape (search fields clear
        // themselves) — but checkboxes/radios don't, and after clicking the
        // header checkbox focus SITS on it, so those must not swallow the
        // clear.
        const input = target.closest?.('input') as HTMLInputElement | null;
        if (input && input.type !== 'checkbox' && input.type !== 'radio') {
          return;
        }
      }
      onRowSelectionChangeRef.current?.({});
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [checkboxReveal, revealHasSelection]);

  const effectiveRowProps = useMemo(() => {
    if (checkboxReveal) {
      // Layer ONLY the checkbox-visibility CSS over the caller's row props —
      // onClick / testids / sx all stay caller-owned. The select column is
      // always the leading td (MRT invariant), hence `td:first-of-type`.
      return (ctx: unknown) => {
        const baseRaw =
          typeof callerRowProps === 'function'
            ? (callerRowProps as (
                c: unknown,
              ) => Record<string, unknown> | undefined)(ctx)
            : (callerRowProps as Record<string, unknown> | undefined);
        const base = baseRaw ?? {};
        const revealSx = revealHasSelection
          ? {} // something is selected → every checkbox stays visible
          : {
              '& td:first-of-type .MuiCheckbox-root': {
                visibility: 'hidden',
              },
              '&:hover td:first-of-type .MuiCheckbox-root': {
                visibility: 'visible',
              },
              // MRT sets `data-selected` on selected trs — reveal without a
              // JS re-render when the row itself is the selected one.
              '&[data-selected="true"] td:first-of-type .MuiCheckbox-root': {
                visibility: 'visible',
              },
            };
        return {
          hover: true,
          ...base,
          sx: {
            ...revealSx,
            ...((base as { sx?: Record<string, unknown> }).sx ?? {}),
          },
        };
      };
    }
    if (!effectiveSelectionMode) return callerRowProps;
    // Return a function so MRT passes the row context in — we need it to call
    // `row.toggleSelected()` on click.
    return (ctx: {
      row: { toggleSelected: () => void; getCanSelect: () => boolean };
    }) => {
      const baseRaw =
        typeof callerRowProps === 'function'
          ? (callerRowProps as (
              ctx: unknown,
            ) => Record<string, unknown> | undefined)(ctx)
          : (callerRowProps as Record<string, unknown> | undefined);
      const base = baseRaw ?? {};
      const onClick = ctx.row.getCanSelect()
        ? () => ctx.row.toggleSelected()
        : (base as { onClick?: unknown }).onClick;
      return {
        ...base,
        hover: true,
        onClick,
        sx: {
          cursor: 'pointer',
          ...((base as { sx?: Record<string, unknown> }).sx ?? {}),
        },
      };
    };
  }, [checkboxReveal, revealHasSelection, effectiveSelectionMode, callerRowProps]);

  // Toggle MRT's filter engine AND suppress its default UI chrome. We render
  // the only visible input (the expanded TextField inside the header cell);
  // MRT's built-in filter row / search-icon button must stay hidden.
  const searchFilterFlags: Record<string, unknown> =
    enableSearch && searchInColumn
      ? searchMode === 'column'
        ? {
            enableColumnFilters: true,
            // 'custom' mode: MRT manages filter state but renders no UI of
            // its own — we own the presentation via our Header wrap.
            columnFilterDisplayMode: 'custom',
          }
        : {
            enableGlobalFilter: true,
            // 'none' mode: MRT does not render its default search input in
            // the top toolbar — our Header wrap is the only input.
            positionGlobalFilter: 'none',
            // Even with positionGlobalFilter: 'none', MRT still renders a
            // "Show/Hide search" toggle button in the top toolbar's internal
            // actions area. Since SDATATABLE_DEFAULTS already disables every
            // other internal action (column actions, density, fullscreen,
            // hiding), we can safely blank the internal-actions slot so the
            // search toggle is not orphaned there either. MRT treats `null`
            // as "use default" — we return an empty fragment explicitly.
            renderToolbarInternalActions: () => <React.Fragment />,
          }
      : {};

  // Pagination is opt-in (see SDATATABLE_DEFAULTS). When a consumer opts in
  // we auto-unlock the themed bottom toolbar so the page controls are
  // actually visible; the consumer's own toolbar/pagination props (spread
  // after these flags) still win.
  const paginationFlags: Record<string, unknown> =
    (mrtProps as { enablePagination?: boolean }).enablePagination === true
      ? {
          enableBottomToolbar: true,
          muiBottomToolbarProps: {
            sx: {
              borderTop: '1px solid',
              borderColor: 'divider',
            },
          },
          muiPaginationProps: {
            rowsPerPageOptions: [10, 25, 50, 100],
            showFirstButton: true,
            showLastButton: true,
          },
        }
      : {};

  // Table props: the caller's (object or callback) + the select-column CSS.
  // The caller's own table sx is applied LAST so an explicit override wins.
  // A caller object REPLACES the locked defaults (size 'small'), as before.
  const layoutMode = (mrtProps as { layoutMode?: unknown }).layoutMode;
  const effectiveTableProps = useMemo(() => {
    const selectColumnTableSx = buildSelectColumnTableSx(layoutMode);
    const withSelectColumnSx = (base: Record<string, unknown> | undefined) => {
      const resolved = base ?? SDATATABLE_DEFAULTS.muiTableProps;
      return {
        ...resolved,
        sx: mergeSx(selectColumnTableSx, (resolved as { sx?: SxInput }).sx),
      };
    };

    if (typeof callerTableProps === 'function') {
      return (ctx: unknown) =>
        withSelectColumnSx(
          (callerTableProps as (ctx: unknown) => Record<string, unknown> | undefined)(ctx),
        );
    }
    return withSelectColumnSx(callerTableProps as Record<string, unknown> | undefined);
  }, [callerTableProps, layoutMode]);

  // A selectable table in semantic layout must have exactly one `width: auto`
  // column, or the leftover width is spread over every column and the 28px
  // select gutter widens with the window (catastrophically so in WebKit — see
  // `buildSelectColumnTableSx`). Honour `grow: true` when a column declares it;
  // otherwise default the first column to the absorber.
  const hasSelectColumn = Boolean(
    checkboxReveal ||
      effectiveSelectionMode ||
      (mrtProps as { enableRowSelection?: unknown }).enableRowSelection,
  );
  const isGridLayout = typeof layoutMode === 'string' && layoutMode.startsWith('grid');
  const needsDefaultAbsorber =
    hasSelectColumn && !isGridLayout && !hasGrowColumn(transformedColumns as unknown[] | undefined);

  const columnsWithGrow = useMemo(() => {
    const marked = markGrowColumns(transformedColumns as unknown[] | undefined);
    return needsDefaultAbsorber ? markFirstLeafColumn(marked) : marked;
  }, [transformedColumns, needsDefaultAbsorber]);

  const effectiveMrtProps = useMemo(() => ({
    ...(mrtProps as Record<string, unknown>),
    ...(effectiveTableProps ? { muiTableProps: effectiveTableProps } : {}),
  }), [mrtProps, effectiveTableProps]);

  return (
    <MaterialReactTable
      {...(SDATATABLE_DEFAULTS as any)}
      {...searchFilterFlags}
      {...selectionModeFlags}
      {...(checkboxRevealFlags as any)}
      {...(selectColumnFlags as any)}
      {...paginationFlags}
      {...(effectiveMrtProps as any)}
      columns={columnsWithGrow as MRT_ColumnDef<TData>[]}
      muiTableBodyRowProps={effectiveRowProps as any}
    />
  );
}

/**
 * Memoised export. We memo at this layer because MRT internally memos the
 * table instance, but the outer wrapper still benefits when a parent
 * re-renders with identical columns + data references.
 *
 * Note: generic components can't be wrapped in `React.memo` directly without
 * losing type inference, so we export a cast version that preserves the
 * generic signature.
 */
export const SDataTable = React.memo(SDataTableInner) as typeof SDataTableInner;

// displayName set via assignment to the memo wrapper — React DevTools friendly.
(SDataTable as unknown as { displayName: string }).displayName = 'SDataTable';

export default SDataTable;
